import { Button, buttonClassName, Chip, Input, Select, useDialog } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  useCreateCheckoutSessionMutation,
  useGetSubscriptionQuery,
  useOpenPortalMutation,
} from '@/features/billing/billingApi';
import { type Branding,useGetBrandingQuery, useUpdateBrandingMutation } from '@/features/branding/brandingApi';
import {
  useGetFeatureFlagsQuery,
  useResetFeatureFlagMutation,
  useSetFeatureFlagMutation,
} from '@/features/feature-flags/featureFlagsApi';
import {
  useExportMeMutation,
  useForgetMeMutation,
} from '@/features/gdpr/gdprApi';
import {
  useInviteMemberMutation,
  useListInvitationsQuery,
  useListMembersQuery,
  useRemoveMemberMutation,
  useRevokeInvitationMutation,
  useUpdateMemberRoleMutation,
} from '@/features/members/membersApi';
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENTS,
  type NotificationChannel,
  type NotificationChannelConfig,
  type NotificationEvent,
  type NotificationMatrix,
  useDeleteOrgMutation,
  useGetCurrentOrgQuery,
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
  useUpdateOrgMutation,
} from '@/features/organizations/organizationsApi';
import {
  useDeleteAssetMutation,
  useListAssetsQuery,
  useStorageUsageQuery,
} from '@/features/uploads/uploadsApi';
import { t } from '@/lib/i18n';
import { useAppSelector } from '@/store/hooks';

const TABS = ['General', 'Branding', 'Team', 'Notifications', 'Storage', 'Billing', 'Feature flags', 'Danger'] as const;
type Tab = (typeof TABS)[number];

export function SettingsScreen() {
  const activeOrgId = useAppSelector((s) => s.auth.activeOrganizationId);
  const myRole = useAppSelector((s) =>
    s.auth.memberships.find((m) => m.organizationId === activeOrgId)?.role,
  );
  const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';
  const visibleTabs = TABS.filter((t) => t !== 'Feature flags' || isOwnerOrAdmin);
  const [tab, setTab] = useState<Tab>('General');

  return (
    <>
      <TopBar crumbs={[t('crumbs.settings')]} />
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div
          style={{
            borderBottom: '1px solid var(--line)',
            background: 'var(--paper)',
            padding: '0 24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 24,
              maxWidth: 1040,
              margin: '0 auto',
            }}
          >
            {visibleTabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '16px 0',
                  fontSize: 13,
                  fontWeight: tab === t ? 600 : 500,
                  color: tab === t ? 'var(--ink)' : 'var(--ink-3)',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: `2px solid ${tab === t ? 'var(--flame)' : 'transparent'}`,
                  cursor: 'pointer',
                }}
              >
                {t}
                {t === 'Danger' && <span style={{ color: 'var(--err)', marginLeft: 4 }}>·</span>}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1040, margin: '0 auto', padding: 24 }}>
          {tab === 'General' && <GeneralTab />}
          {tab === 'Branding' && <BrandingTab />}
          {tab === 'Team' && <TeamTab />}
          {tab === 'Notifications' && <NotificationsTab />}
          {tab === 'Storage' && <StorageTab />}
          {tab === 'Billing' && <BillingTab />}
          {tab === 'Feature flags' && isOwnerOrAdmin && <FeatureFlagsTab />}
          {tab === 'Danger' && <DangerTab />}
        </div>
      </div>
    </>
  );
}

function GeneralTab() {
  const { data: org } = useGetCurrentOrgQuery();
  const [update, { isLoading: isSaving }] = useUpdateOrgMutation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (org) {
      setName(org.name);
      setSlug(org.slug);
    }
  }, [org]);

  const canSave = Boolean(org) && (name !== org?.name || slug !== org?.slug) && name.trim().length > 0;

  const save = async () => {
    setErrorMsg(null);
    try {
      await update({ name: name.trim(), slug: slug.trim() }).unwrap();
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2500);
    } catch (e: unknown) {
      setStatus('error');
      setErrorMsg((e as { data?: { message?: string } })?.data?.message ?? 'Could not save workspace.');
    }
  };

  return (
    <Section
      title="Workspace"
      description="Basic details about your Emailforge workspace."
      actions={
        <Button variant="flame" size="sm" onClick={save} disabled={!canSave || isSaving}>
          {isSaving ? 'Saving…' : status === 'saved' ? 'Saved' : 'Save changes'}
        </Button>
      }
    >
      <FormRow label="Workspace name">
        <Input  value={name} onChange={(e) => setName(e.target.value)} />
      </FormRow>
      <FormRow label="Subdomain">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())} />
          <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            .emailforge.app
          </span>
        </div>
      </FormRow>
      <FormRow label="Plan">
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {org?.planCode ?? '—'}
          {org?.trialEndsAt && (
            <span className="mono" style={{ marginLeft: 10, color: 'var(--ink-3)', fontSize: 11 }}>
              · trial ends {new Date(org.trialEndsAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </FormRow>
      {errorMsg && (
        <div style={{ fontSize: 12.5, color: 'var(--err)' }}>{errorMsg}</div>
      )}
    </Section>
  );
}

function BrandingTab() {
  const { data } = useGetBrandingQuery();
  const [update, { isLoading: isSaving }] = useUpdateBrandingMutation();
  const [draft, setDraft] = useState<Branding>({
    logoUrl: null,
    faviconUrl: null,
    customDomain: null,
    colors: {},
    fonts: {},
  });

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = async () => {
    await update(draft).unwrap();
    const { applyBranding } = await import('@/features/branding/applyBranding');
    applyBranding(draft);
  };

  return (
    <Section
      title="Branding"
      description="Logo, colors, fonts and custom domain for your workspace. Changes apply instantly via CSS variables."
      actions={
        <Button variant="flame" size="sm" onClick={save} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save branding'}
        </Button>
      }
    >
      <FormRow label="Logo URL">
        <Input
          
          value={draft.logoUrl ?? ''}
          onChange={(e) => setDraft({ ...draft, logoUrl: e.target.value || null })}
          placeholder="https://cdn.your-domain.com/logo.svg" />
      </FormRow>
      <FormRow label="Favicon URL">
        <Input
          
          value={draft.faviconUrl ?? ''}
          onChange={(e) => setDraft({ ...draft, faviconUrl: e.target.value || null })}
          placeholder="https://cdn.your-domain.com/favicon.ico" />
      </FormRow>
      <FormRow label="Custom domain">
        <Input mono
          
          value={draft.customDomain ?? ''}
          onChange={(e) => setDraft({ ...draft, customDomain: e.target.value || null })}
          placeholder="app.yourdomain.com" />
      </FormRow>
      <FormRow label="Primary color">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 40,
              height: 28,
              borderRadius: 'var(--r-2)',
              border: '1px solid var(--line)',
              background: draft.colors['primary'] ?? 'var(--flame)',
            }}
          />
          <Input mono
            
            value={draft.colors['primary'] ?? ''}
            onChange={(e) => setDraft({ ...draft, colors: { ...draft.colors, primary: e.target.value } })}
            placeholder="oklch(0.635 0.195 32)" />
        </div>
      </FormRow>
      <FormRow label="Display font">
        <Input mono
          
          value={draft.fonts['display'] ?? ''}
          onChange={(e) => setDraft({ ...draft, fonts: { ...draft.fonts, display: e.target.value } })}
          placeholder='"Fraunces", serif' />
      </FormRow>
    </Section>
  );
}

function TeamTab() {
  const { data: members } = useListMembersQuery();
  const { data: invitations } = useListInvitationsQuery();
  const [invite, { isLoading: isInviting }] = useInviteMemberMutation();
  const [revoke] = useRevokeInvitationMutation();
  const [updateRole] = useUpdateMemberRoleMutation();
  const [remove] = useRemoveMemberMutation();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const { confirm } = useDialog();

  const submitInvite = async () => {
    if (!inviteEmail) return;
    await invite({ email: inviteEmail, role: inviteRole }).unwrap();
    setInviteEmail('');
  };

  return (
    <Section
      title="Members"
      description="Invite teammates, manage roles, revoke pending invitations. Invitations expire in 72 hours."
    >
      <div
        className="mf-card"
        style={{
          padding: 14,
          display: 'grid',
          gridTemplateColumns: '1.6fr 160px auto',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <Input
          
          placeholder="teammate@company.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)} />
        <Select  value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'admin' | 'editor' | 'viewer')}>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </Select>
        <Button variant="flame" size="sm" onClick={submitInvite} disabled={!inviteEmail || isInviting}>
          <Icon.plus size={13} /> {isInviting ? 'Sending…' : 'Invite'}
        </Button>
      </div>

      {invitations && invitations.length > 0 && (
        <div className="mf-card" style={{ overflow: 'hidden' }}>
          <div
            className="mf-card-head"
            style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.08 }}
          >
            <span>Pending invitations</span>
          </div>
          <table className="mf-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id}>
                  <td style={{ fontSize: 13 }}>{inv.email}</td>
                  <td>
                    <Chip>{inv.role}
                    </Chip>
                  </td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {new Date(inv.expiresAt).toLocaleString()}
                  </td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={async () => {
                        if (await confirm({
                          title: 'Revoke invitation?',
                          description: `${inv.email} will not be able to use this invite link anymore. You can re-invite them later.`,
                          confirmLabel: 'Revoke',
                          destructive: true,
                        })) await revoke(inv.id).unwrap();
                      }}
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mf-card" style={{ overflow: 'hidden' }}>
        <table className="mf-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m.userId}>
                <td style={{ fontSize: 13, fontWeight: 500 }}>
                  {m.user.name ?? m.user.email}
                  {m.user.name && (
                    <span className="mono" style={{ marginLeft: 6, color: 'var(--ink-3)', fontSize: 11 }}>
                      · {m.user.email}
                    </span>
                  )}
                </td>
                <td>
                  <Select
                    
                    style={{ height: 24, padding: '0 6px' }}
                    value={m.role}
                    onChange={(e) => updateRole({ userId: m.userId, role: e.target.value as typeof m.role })}
                    disabled={m.role === 'owner'}>
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </Select>
                </td>
                <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                  {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}
                </td>
                <td>
                  {m.role !== 'owner' && (
                    <Button variant="ghost" size="sm" icon onClick={async () => {
                        if (await confirm({
                          title: 'Remove member?',
                          description: `${m.user.email} will lose access to this workspace immediately. Their personal account stays.`,
                          confirmLabel: 'Remove',
                          destructive: true,
                        })) await remove(m.userId).unwrap();
                      }}
                    >
                      <Icon.trash size={12} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {(members ?? []).length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--ink-3)' }}>
                  Loading members…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function NotificationsTab() {
  const { data, isLoading } = useGetNotificationsQuery();
  const [save, { isLoading: isSaving }] = useUpdateNotificationsMutation();
  const [prefs, setPrefs] = useState<NotificationMatrix>({});
  const [channels, setChannels] = useState<NotificationChannelConfig>({});
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (data) {
      setPrefs(data.prefs);
      setChannels(data.channels);
    }
  }, [data]);

  const toggle = (event: NotificationEvent, channel: NotificationChannel) => {
    setPrefs((current) => {
      const eventPrefs = { ...(current[event] ?? {}) };
      eventPrefs[channel] = !eventPrefs[channel];
      return { ...current, [event]: eventPrefs };
    });
  };

  const onSave = async () => {
    try {
      await save({ prefs, channels }).unwrap();
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2500);
    } catch {
      setStatus('error');
    }
  };

  if (isLoading) {
    return <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>;
  }

  return (
    <Section
      title="Notifications"
      description="Route platform events to the channels your team monitors. Slack and webhook channels need a URL configured below."
      actions={
        <Button variant="flame" size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : status === 'saved' ? 'Saved' : 'Save changes'}
        </Button>
      }
    >
      <div
        className="mf-card"
        style={{ overflow: 'hidden', borderRadius: 'var(--r-2)', border: '1px solid var(--line)' }}
      >
        <table className="mf-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Event</th>
              {NOTIFICATION_CHANNELS.map((c) => (
                <th key={c} style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_EVENTS.map((ev) => (
              <tr key={ev}>
                <td className="mono" style={{ fontSize: 12 }}>
                  {ev}
                </td>
                {NOTIFICATION_CHANNELS.map((ch) => (
                  <td key={ch} style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(prefs[ev]?.[ch])}
                      onChange={() => toggle(ev, ch)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 24 }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            color: 'var(--ink-3)',
            marginBottom: 8,
          }}
        >
          Channel destinations
        </div>
        <FormRow label="Digest email">
          <Input
            type="email"
            value={channels.digestEmail ?? ''}
            placeholder="alerts@acme.inc"
            onChange={(e) => setChannels((c) => ({ ...c, digestEmail: e.target.value || null }))}
          />
        </FormRow>
        <FormRow label="Slack incoming webhook URL">
          <Input
            type="url"
            value={channels.slackWebhookUrl ?? ''}
            placeholder="https://hooks.slack.com/services/…"
            onChange={(e) => setChannels((c) => ({ ...c, slackWebhookUrl: e.target.value || null }))}
          />
        </FormRow>
        <FormRow label="Slack channel">
          <Input
            value={channels.slackChannel ?? ''}
            placeholder="#email-ops"
            onChange={(e) => setChannels((c) => ({ ...c, slackChannel: e.target.value || null }))}
          />
        </FormRow>
        <FormRow label="Outbound webhook URL">
          <Input
            type="url"
            value={channels.outboundWebhookUrl ?? ''}
            placeholder="https://hooks.your-app.com/email-events"
            onChange={(e) => setChannels((c) => ({ ...c, outboundWebhookUrl: e.target.value || null }))}
          />
        </FormRow>
      </div>
    </Section>
  );
}

function StorageTab() {
  const { data: usage, isLoading: usageLoading } = useStorageUsageQuery();
  const { data: assets = [], isLoading: assetsLoading } = useListAssetsQuery();
  const [deleteAsset, { isLoading: isDeleting }] = useDeleteAssetMutation();

  const pct = usage && usage.bytesQuota > 0 ? Math.min(100, (usage.bytesUsed / usage.bytesQuota) * 100) : 0;

  return (
    <Section
      title="Storage"
      description="Image assets uploaded by your team. Files served from a global CDN; deleting here removes the file from storage immediately."
    >
      <div
        className="mf-card"
        style={{ padding: 18, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 0.06, textTransform: 'uppercase' }}>
          Plan quota
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="display" style={{ fontSize: 24, fontWeight: 500 }}>
            {usage ? formatBytes(usage.bytesUsed) : '—'}
          </span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            of {usage ? formatBytes(usage.bytesQuota) : '—'} ({usage?.fileCount ?? 0} files)
          </span>
        </div>
        <div
          style={{
            height: 6,
            background: 'var(--paper-4)',
            borderRadius: 'var(--r-pill)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: pct > 90 ? 'var(--err)' : pct > 70 ? 'var(--warn, #b48323)' : 'var(--flame)',
              transition: 'width 300ms ease',
            }}
          />
        </div>
      </div>

      {(assetsLoading || usageLoading) && (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      )}

      {!assetsLoading && assets.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: 20 }}>
          No uploads yet. Drop an image into any image / hero / product block in the editor.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {assets.map((a) => (
          <div
            key={a.id}
            style={{
              border: '1px solid var(--line-soft)',
              borderRadius: 'var(--r-2)',
              background: 'var(--paper)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                aspectRatio: '4 / 3',
                background: 'var(--paper-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={a.url}
                alt={a.filename}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={a.filename}
                >
                  {a.filename}
                </div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  {formatBytes(a.sizeBytes)} · {a.contentType.replace('image/', '')}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(`Delete "${a.filename}"? This cannot be undone.`)) deleteAsset(a.id);
                }}
                disabled={isDeleting}
                aria-label="Delete"
              >
                <Icon.trash size={11} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function BillingTab() {
  const { data, isLoading } = useGetSubscriptionQuery();
  const [openPortal, portalMut] = useOpenPortalMutation();
  const [createCheckout, checkoutMut] = useCreateCheckoutSessionMutation();

  const planLabel = data?.plan ? data.plan.charAt(0).toUpperCase() + data.plan.slice(1) : isLoading ? '…' : 'Free';
  const hasActiveSub = data?.subscription != null;
  const periodEnd = data?.subscription?.currentPeriodEnd;
  const canceledAt = data?.subscription?.canceledAt;

  const manage = async () => {
    const res = await openPortal().unwrap();
    window.location.href = res.url;
  };

  const upgrade = async (plan: 'starter' | 'pro') => {
    const res = await createCheckout({ plan, cycle: 'monthly' }).unwrap();
    window.location.href = res.url;
  };

  return (
    <Section title="Billing" description="Your subscription, invoices, and usage for the current period.">
      <div className="mf-card" style={{ padding: 20 }}>
        <div
          className="mono"
          style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
        >
          Current plan
        </div>
        <div className="display" style={{ fontSize: 28, fontWeight: 500, margin: '6px 0 4px' }}>
          {planLabel}
        </div>
        {hasActiveSub ? (
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {canceledAt
              ? `Canceling — access until ${periodEnd ? new Date(periodEnd).toLocaleDateString() : '—'}.`
              : `Renews ${periodEnd ? new Date(periodEnd).toLocaleDateString() : 'monthly'}.`}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            Upgrade to Pro for unlimited templates and 10,000 sends/month.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {hasActiveSub ? (
            <Button variant="flame" size="sm" onClick={manage} disabled={portalMut.isLoading}>
              {portalMut.isLoading ? 'Opening…' : 'Manage in Stripe portal'}
            </Button>
          ) : (
            <>
              <Button size="sm" onClick={() => upgrade('starter')}
                disabled={checkoutMut.isLoading}
              >
                Upgrade to Starter
              </Button>
              <Button variant="flame" size="sm" onClick={() => upgrade('pro')}
                disabled={checkoutMut.isLoading}
              >
                {checkoutMut.isLoading ? 'Redirecting…' : 'Upgrade to Pro'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Section>
  );
}

function FeatureFlagsTab() {
  const { data, isLoading } = useGetFeatureFlagsQuery();
  const [setFlag, setMut] = useSetFeatureFlagMutation();
  const [resetFlag, resetMut] = useResetFeatureFlagMutation();
  const pending = setMut.isLoading || resetMut.isLoading;

  return (
    <Section
      title="Feature flags"
      description="Per-workspace overrides on platform features. Defaults come from your plan. Changes take effect within 5 minutes (Redis cache)."
    >
      {isLoading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}
      <div className="mf-card" style={{ overflow: 'hidden' }}>
        <table className="mf-table">
          <thead>
            <tr>
              <th>Flag</th>
              <th>Description</th>
              <th style={{ width: 90 }}>Default</th>
              <th style={{ width: 110 }}>Effective</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {(data?.catalog ?? []).map((def) => {
              const effective = data?.effective[def.key] ?? def.default;
              const overridden = effective !== def.default;
              return (
                <tr key={def.key}>
                  <td className="mono" style={{ fontSize: 12 }}>{def.key}</td>
                  <td style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{def.description}</td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {def.default ? 'on' : 'off'}
                  </td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={effective}
                        disabled={pending}
                        onChange={(e) => setFlag({ key: def.key, enabled: e.target.checked }).unwrap()}
                      />
                      <Chip variant={effective ? 'live' : 'default'}>{effective ? 'on' : 'off'}</Chip>
                    </label>
                  </td>
                  <td>
                    {overridden && (
                      <Button variant="ghost" size="sm" onClick={() => resetFlag(def.key).unwrap()}
                        disabled={pending}
                      >
                        Reset
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function DangerTab() {
  const { data: org } = useGetCurrentOrgQuery();
  const [deleteOrg, delMut] = useDeleteOrgMutation();
  const [exportMe, exportMut] = useExportMeMutation();
  const [forgetMe, forgetMut] = useForgetMeMutation();
  const [confirmText, setConfirmText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const { confirm } = useDialog();

  const canDelete = org && confirmText.trim().toLowerCase() === org.slug.toLowerCase();

  const handleDelete = async () => {
    if (!canDelete) return;
    setErr(null);
    try {
      await deleteOrg().unwrap();
      navigate('/');
    } catch (e: unknown) {
      setErr((e as { data?: { message?: string } })?.data?.message ?? 'Could not delete workspace.');
    }
  };

  const downloadMyData = async () => {
    try {
      const data = await exportMe().unwrap();
      downloadJson(data, `emailforge-user-export-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e: unknown) {
      setErr((e as { data?: { message?: string } })?.data?.message ?? 'Export failed.');
    }
  };

  const deleteMyAccount = async () => {
    if (
      !(await confirm({
        title: 'Delete my account?',
        description:
          'Your personal account will be anonymized and you will be signed out from every workspace. Workspaces you owned alone will be suspended. This is irreversible.',
        confirmLabel: 'Delete account',
        destructive: true,
      }))
    ) {
      return;
    }
    try {
      await forgetMe().unwrap();
      navigate('/login');
    } catch (e: unknown) {
      setErr((e as { data?: { message?: string } })?.data?.message ?? 'Account deletion failed.');
    }
  };

  return (
    <Section title="Danger zone" description="Irreversible actions — be sure.">
      <ComplianceDocsCard />
      <div className="mf-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 550, marginBottom: 6 }}>Your data (GDPR)</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
          Download everything we store about you, or anonymize your personal account. These apply to your user —
          not to this workspace.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={downloadMyData} disabled={exportMut.isLoading}>
            <Icon.download size={12} /> {exportMut.isLoading ? 'Exporting…' : 'Export my data'}
          </Button>
          <Button size="sm" style={{ color: 'var(--err)' }}
            onClick={deleteMyAccount}
            disabled={forgetMut.isLoading}>
            {forgetMut.isLoading ? 'Deleting…' : 'Delete my account'}
          </Button>
        </div>
      </div>

      <div className="mf-card" style={{ padding: 20, borderColor: 'oklch(0.85 0.10 25)' }}>
        <div style={{ fontSize: 14, fontWeight: 550, marginBottom: 6 }}>Delete workspace</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
          Permanently suspends this workspace, templates, experiments, and send events. Only the owner can do this.
          Type <span className="mono" style={{ color: 'var(--ink)' }}>{org?.slug ?? '…'}</span> to confirm.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Input
            
            style={{ maxWidth: 280 }}
            placeholder={org?.slug ?? 'workspace-slug'}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)} />
          <Button size="sm" style={{ background: 'var(--err)', color: 'white', border: 'none', opacity: canDelete ? 1 : 0.5 }}
            disabled={!canDelete || delMut.isLoading}
            onClick={handleDelete}>
            {delMut.isLoading ? 'Deleting…' : 'Delete workspace permanently'}
          </Button>
        </div>
        {err && <div style={{ fontSize: 12.5, color: 'var(--err)' }}>{err}</div>}
      </div>
    </Section>
  );
}

/**
 * Renders three "open + print as PDF" links for the legal docs that auditors
 * routinely ask for. The pages themselves carry full content; opening with
 * `?print=1` triggers the browser's print dialog so the user can save as PDF
 * without us shipping a pdfkit dependency.
 */
function ComplianceDocsCard() {
  const docs: Array<{ slug: string; title: string; description: string }> = [
    { slug: 'dpa', title: 'Data Processing Agreement (DPA)', description: 'Required for EU/UK/Swiss data subjects under Art. 28 GDPR.' },
    { slug: 'privacy', title: 'Privacy Policy', description: 'What we collect, how long we keep it, and your rights.' },
    { slug: 'terms', title: 'Terms of Service', description: 'Acceptable use, billing, IP, and liability.' },
  ];
  return (
    <div className="mf-card" style={{ padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 550, marginBottom: 6 }}>Compliance documents</div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14 }}>
        Download a PDF copy for your records or to send to your DPO. "Save as PDF" is in the print dialog.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {docs.map((d) => (
          <div key={d.slug} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{d.title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.4 }}>{d.description}</div>
            </div>
            <a
              className={buttonClassName({ variant: 'ghost', size: 'sm' })}
              href={`/${d.slug}?print=1`}
              target="_blank"
              rel="noreferrer"
              style={{ flexShrink: 0 }}
            >
              <Icon.download size={12} /> Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 32, marginBottom: 40 }}>
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>{title}</h3>
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, margin: 0 }}>{description}</p>
        {actions && <div style={{ marginTop: 16 }}>{actions}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, alignItems: 'center' }}>
      <label style={{ fontSize: 12.5, color: 'var(--ink-2)', fontWeight: 500 }}>{label}</label>
      <div>{children}</div>
    </div>
  );
}
